import { getAuthToken, getAuthUser } from '@/lib/api/token-store';
import {
  isAttentionCallLogMessage,
  isStandaloneSystemLineMessage,
} from '@/lib/realtime/chat-message-helpers';
import { getUserIdsFromJwt } from '@/lib/realtime/jwt-user-ids';

/**
 * Primary user id for readBy checks (profile id, else first JWT candidate).
 */
export function getCurrentUserIdForReadReceipts() {
  const fromUser = String(getAuthUser()?.id || '').trim();
  if (fromUser) {
    return fromUser;
  }
  const ids = getUserIdsFromJwt(getAuthToken());
  return ids[0] ? String(ids[0]).trim() : '';
}

export function userHasReadMessage(message, userId) {
  const uid = String(userId || '').trim();
  if (!uid) {
    return false;
  }
  const list = message?.readBy;
  if (!Array.isArray(list)) {
    return false;
  }
  return list.some((entry) => entry && String(entry.userId || '').trim() === uid);
}

export function userWasDeliveredMessage(message, userId) {
  const uid = String(userId || '').trim();
  if (!uid) {
    return false;
  }
  const list = message?.deliveredTo;
  if (!Array.isArray(list)) {
    return false;
  }
  return list.some((entry) => entry && String(entry.userId || '').trim() === uid);
}

export function isOwnMessageForRead(message, userId) {
  const senderId = String(message?.sender?.userId || '').trim();
  if (!senderId) {
    return false;
  }
  const current = String(userId || '').trim();
  if (current && senderId === current) {
    return true;
  }
  return getUserIdsFromJwt(getAuthToken()).includes(senderId);
}

/**
 * Incoming WS message for a thread that is not focused: show red dot (other user / bot, not system).
 */
export function shouldFlagThreadUnreadFromWsMessage(message, userId) {
  if (!message || !String(message.threadId || '').trim()) {
    return false;
  }
  if (!String(userId || '').trim()) {
    return false;
  }
  // System lines never flag unread — except a missed/declined call, which is
  // a patient trying to reach the clinic and needs the red dot.
  if (isStandaloneSystemLineMessage(message) && !isAttentionCallLogMessage(message)) {
    return false;
  }
  return !isOwnMessageForRead(message, userId);
}

/**
 * Whether to send a read receipt for this message (open conversation, not own, not system, not already read).
 */
export function messageShouldMarkRead(message, userId) {
  if (!message || typeof message !== 'object') {
    return false;
  }
  const id = String(message.id || '').trim();
  if (!id) {
    return false;
  }
  if (!String(userId || '').trim()) {
    return false;
  }
  if (isOwnMessageForRead(message, userId)) {
    return false;
  }
  if (isStandaloneSystemLineMessage(message)) {
    return false;
  }
  if (userHasReadMessage(message, userId)) {
    return false;
  }
  return true;
}

/**
 * Whether to ack DELIVERY for this message.
 *
 * Looser than messageShouldMarkRead on purpose. Reading is a human act, so system lines are
 * excluded there; receiving is not, so a system line still counts as delivered. Messages arriving
 * over a live socket are stamped server-side, so in practice this only fires for what the client
 * picked up from history after being offline.
 */
export function shouldAckDelivered(message, userId) {
  if (!message || typeof message !== 'object') {
    return false;
  }
  if (!String(message.id || '').trim()) {
    return false;
  }
  if (!String(userId || '').trim()) {
    return false;
  }
  if (message.deleted === true) {
    return false;
  }
  if (isOwnMessageForRead(message, userId)) {
    return false;
  }
  return !userWasDeliveredMessage(message, userId);
}
